import React from "react";
import { Skeleton } from "components/ui/skeleton";

export function IdentityTableSkeleton() {
  return (
    <div className="card hover-lift fade-in">
      <table className="w-full border-collapse">
        {/* Table Header */}
        <thead className="border-b border-line-subtle dark:border-dark-600 bg-gray-50 dark:bg-dark-800">
          <tr>
            {Array.from({ length: 5 }).map((_, i) => (
              <th
                key={i}
                className="text-left px-6 py-3 text-sm font-semibold text-content-secondary dark:text-gray-300"
              >
                {/* Header skeletons with slightly different widths */}
                <Skeleton
                  variant="text"
                  className={`!h-4 ${i === 0
                    ? "!w-16"
                    : i === 1
                      ? "!w-36"
                      : i === 2
                        ? "!w-28"
                        : i === 3
                          ? "!w-32"
                          : "!w-24"
                    }`}
                />
              </th>
            ))}
          </tr>
        </thead>

        {/* Table Body */}
        <tbody className="divide-y divide-gray-100 dark:divide-dark-600">
          {Array.from({ length: 5 }).map((_, i) => (
            <tr key={i} className="animate-pulse">
              {/* Actions */}
              <td className="px-6 py-4">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-6 w-6 rounded-full" />
                  <Skeleton className="h-6 w-6 rounded-full" />
                </div>
              </td>

              {/* Email */}
              <td className="px-6 py-4">
                <Skeleton variant="text" className="!h-4 !w-56" />
              </td>

              {/* Verification Status */}
              <td className="px-6 py-4">
                <Skeleton variant="chip" className="h-6 w-20" />
              </td>

              {/* Business Name */}
              <td className="px-6 py-4">
                <Skeleton variant="text" className="!h-4 !w-40" />
              </td>

              {/* Status Button / Badge */}
              <td className="px-6 py-4">
                <Skeleton variant="chip" className="h-6 w-24" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function IdentityMobileCardSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="relative overflow-hidden bg-surface dark:bg-dark-800 rounded-2xl border border-line dark:border-dark-700 shadow-sm animate-pulse"
        >
          {/* Accent bar skeleton */}
          <Skeleton className="absolute top-0 inset-x-0 !h-[2.5px] !w-full !rounded-t-2xl !rounded-b-none" />

          {/* Card body */}
          <div className="px-4 pt-4 pb-3">
            <div className="flex items-start justify-between gap-3">
              {/* Left: dot + email + business name */}
              <div className="flex-1 min-w-0">
                {/* dot + email row */}
                <div className="flex items-center gap-2">
                  <Skeleton className="flex-shrink-0 !w-2 !h-2 !rounded-full" />
                  <Skeleton
                    variant="text"
                    className={`!h-[13px] ${i % 2 === 0 ? "!w-52" : "!w-44"}`}
                  />
                </div>
                {/* business name */}
                <Skeleton
                  variant="text"
                  className={`!h-3 mt-1 ml-4 ${i % 3 === 0 ? "!w-28" : "!w-36"}`}
                />
              </div>

              {/* Right: verification badge */}
              <Skeleton variant="chip" className="flex-shrink-0 !h-5 !w-16 mt-0.5" />
            </div>
          </div>

          {/* Card footer */}
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-line-subtle dark:border-dark-700/60 bg-gray-50/50 dark:bg-dark-700/20">
            {/* Left: verify button / verified badge skeleton */}
            <Skeleton variant="chip" className="!h-6 !w-20" />

            {/* Right: two icon button skeletons */}
            <div className="flex items-center gap-1">
              <Skeleton className="!w-8 !h-8 !rounded-xl" />
              <Skeleton className="!w-8 !h-8 !rounded-xl" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
